import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import type { ComponentType, ReactElement } from "react";

/**
 * Shared gate for the "register a chrome override on mount, restore the
 * default on unmount" pattern used throughout Mobile/hooks (TopBar
 * overrides, trailing actions, hidden chrome, …): renders the given
 * renderless Registrar component only when the mobile shell is enabled
 * and `props` is non-null.
 *
 * Pass `null` for `props` to cover any extra guard a hook needs - e.g. an
 * `enabled` flag, or required data that hasn't loaded yet - since a
 * Registrar's own context hook call (e.g. `useMobileTopBarOverride`,
 * `useMobileChromeVisibility`) must never run outside the
 * MobileSectionTabsProvider.
 *
 * Usage:
 * ```tsx
 * export const useChatSpaceTopBar = ({ space }: { space: Space | undefined }) =>
 *   useMobileShellRegistrar(ChatSpaceTopBarRegistrar, space ? { space } : null);
 * ```
 */
export const useMobileShellRegistrar = <P extends object>(
  Registrar: ComponentType<P>,
  props: P | null,
): ReactElement | null => {
  const isMobile = useIsMobile();
  return isMobile && props ? <Registrar {...props} /> : null;
};
